import { UserData, VenueId } from '@beat-venues/common-types';

import { mapUserToUpdateForm } from '../utils';

const MYBEAT_API = process.env['VITE_MYBEAT_API_URI'];
const SIGNIN_URI = process.env['VITE_SIGNIN_DOMAIN_URI'] as string;
const MYBEAT_DOMAIN = process.env['VITE_MYBEAT_DOMAIN'];

const getParams = (name: string): string | null => {
    if (typeof window !== 'undefined') {
        const results = new RegExp('[?&]' + name + '=([^&#]*)').exec(
            window.location.href
        );
        if (results == null) {
            return null;
        }
        return decodeURI(results[1].trim()) || '';
    }
    return null;
};

const getUserData = async (token: string) => {
    const response = await fetch(`${MYBEAT_API}/user`, {
        method: 'POST',
        body: JSON.stringify({
            token,
        }),
    });
    if (!response.ok) {
        throw new Error('Failed to fetch user');
    }
    return await response.json();
};

const putUserData = async (token: string, userDetails: UserData) => {
    const response = await fetch(`${MYBEAT_API}/user`, {
        method: 'PUT',
        body: JSON.stringify({
            token,
            userDetails: mapUserToUpdateForm(userDetails),
        }),
    });

    if (!response.ok) {
        throw new Error('Failed to update user');
    }

    return await response.json();
};

const getNewToken = async (refreshToken: string): Promise<number> => {
    const response = await fetch('/api/refreshToken/getNewToken', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            refreshToken,
        }),
    }).then((data) => {
        return data.status;
    });
    return await response;
};

const getVenueIdFromUrl = (url: string): VenueId | null => {
    try {
        const urlObj = new URL(url);
        const hostname = urlObj.hostname;

        // Check if it's a Vercel URL
        if (hostname.includes('.vercel.app')) {
            const subdomain = hostname.split('.')[0];

            let baseProject = subdomain.split('-git-')[0];
            if (baseProject === 'stadsschouwburg-antwerp') {
                baseProject = 'stadsschouwburg-antwerpen';
            }
            if (baseProject === 'capitole-ghent') {
                baseProject = 'capitole-gent';
            }
            return baseProject.toUpperCase().replace('-', '_') as VenueId;
        }

        // For regular domains, get the main domain part
        const parts = hostname.split('.');
        const domain = parts.length > 2 ? parts[parts.length - 2] : parts[0];

        return domain.toUpperCase().replace('-', '_') as VenueId;
    } catch (error) {
        console.error('Invalid URL:', error);
        return null;
    }
};

const deleteSession = async () => {
    await fetch(`${MYBEAT_API}/session`, {
        method: 'DELETE',
        credentials: 'include',
    }).catch((error) => {
        console.error('DELETE failed:', error);
    });
};

const getRedirectInfo = (): {
    logoutUrl: string;
    userPoolId: string | undefined;
} => {
    const redirectUrl = sessionStorage.getItem('logout-info');

    let logoutUrl;
    let userPoolId;

    if (redirectUrl) {
        console.log('Determining logout redirect for:', redirectUrl);
        logoutUrl = process.env[
            `VITE_USER_POOL_APP_SSO_CLIENT_ID_${getVenueIdFromUrl(redirectUrl)}`
        ]
            ? `${redirectUrl}auth/callback`
            : document.location.origin;
        userPoolId =
            process.env[
                `VITE_USER_POOL_APP_SSO_CLIENT_ID_${getVenueIdFromUrl(
                    redirectUrl
                )}`
            ] || process.env['VITE_USER_POOL_APP_SSO_CLIENT_ID'];
    } else {
        console.log('No referrer, using default logout redirect');
        logoutUrl = document.location.origin;
        userPoolId = process.env['VITE_USER_POOL_APP_SSO_CLIENT_ID'];
    }
    return { logoutUrl, userPoolId };
};

const logout = async () => {
    const { logoutUrl, userPoolId } = getRedirectInfo();

    const logoutRedirect = `${MYBEAT_API}/logout?client_id=${userPoolId}&logout_uri=${logoutUrl}?logout=true`;

    await deleteSession();
    sessionStorage.removeItem('logout-info');

    setTimeout(() => {
        console.log('Redirecting to logout URL:', logoutRedirect);
        window.location.href = logoutRedirect;
    }, 500);
};

const redirectAfterLogout = (redirectToSignin?: boolean) => {
    const logoutUrl = sessionStorage.getItem('logout-info');
    console.log('Logout URL:', logoutUrl);
    if (!logoutUrl) {
        console.log('No logout info founded, redirecting to signin');
    }
    sessionStorage.removeItem('logout-info');
    const isPresalesPage = ['presales', 'preventes'].some((url) =>
        window.location.href.includes(url)
    );
    if (isPresalesPage) {
        localStorage.setItem('redirect-after-login', window.location.href);
    }
    window.location.href =
        !redirectToSignin && logoutUrl && !isPresalesPage
            ? `${logoutUrl}?logout=true`
            : SIGNIN_URI;
    return;
};

const deleteCookies = () => {
    document.cookie = `token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${MYBEAT_DOMAIN}`;
    document.cookie = `refreshToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${MYBEAT_DOMAIN}`;
    document.cookie = `accessToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${MYBEAT_DOMAIN}`;

    redirectAfterLogout();
};

export {
    getNewToken,
    getUserData,
    logout,
    putUserData,
    redirectAfterLogout,
    deleteCookies,
    getParams,
};
